import { gzip } from 'fflate'
import { splatCacheKey } from '../envCubemapCache.js'
import { buildEnvironmentPackage } from './environmentPackage.js'
import { buildTar } from './tar.js'

// Progress phases reported via the onProgress callback. Each call passes
// { phase, label, loaded?, total? }. `total === 0` means indeterminate.
// Phases: 'downloading' (splat — the big one, byte-accurate %), 'lighting',
// 'packaging', 'compressing', 'done'.
function report(onProgress, p) { try { onProgress?.(p) } catch { /* never let UI throw break export */ } }

// Stream a fetch with byte-level progress so big splats show a real %.
// Falls back to a single arrayBuffer read if the body isn't streamable.
async function fetchBytesWithProgress(url, onProgress, label) {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`environment export: fetch ${res.status} for ${label}`)
  const total = Number(res.headers.get('content-length') || 0)
  const reader = res.body?.getReader?.()
  if (!reader) {
    const buf = new Uint8Array(await res.arrayBuffer())
    report(onProgress, { phase: 'downloading', label, loaded: buf.length, total: buf.length })
    return buf
  }
  const chunks = []
  let loaded = 0
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    chunks.push(value)
    loaded += value.length
    report(onProgress, { phase: 'downloading', label, loaded, total })
  }
  const out = new Uint8Array(loaded)
  let off = 0
  for (const c of chunks) { out.set(c, off); off += c.length }
  return out
}

// Async gzip (fflate runs it off the main thread) so compressing a large tar
// doesn't freeze the UI — the previous gzipSync was the main "looks stuck" cause.
function gzipAsync(data) {
  return new Promise((resolve, reject) => {
    gzip(data, { level: 6 }, (err, out) => (err ? reject(err) : resolve(out)))
  })
}

// Fetch helper: returns Uint8Array if the URL serves the expected content
// type, else null. Vite's SPA fallback serves index.html (text/html) for
// unknown paths, so an explicit content-type check is required — `res.ok`
// alone would misreport a missing asset as present.
async function fetchIfType(url, typeRe) {
  try {
    const res = await fetch(url, { method: 'GET' })
    const ct = res.headers.get('content-type') || ''
    if (!res.ok || !typeRe.test(ct)) return null
    return new Uint8Array(await res.arrayBuffer())
  } catch { return null }
}

// Resolve a splat asset's baked equirect IBL (or null if not baked yet).
async function resolveIblBytes(key) {
  return fetchIfType(`/assets/environments/equirect/${key}.png`, /^image\//)
}

// Resolve the diffuse SH light-probe volume JSON (ENG-561 Phase A), or null.
async function resolveLightprobeBytes(key) {
  return fetchIfType(`/assets/environments/lightprobes/${key}.json`, /json/)
}

// Resolve the specular reflection-capture set (ENG-561 Phase B): the manifest
// plus one PNG per capture, named <i>.png beside it. Returns
// { manifestBytes, captures: [{ i, bytes }] } or null if not baked.
async function resolveReflectionSet(key) {
  const base = `/assets/environments/reflections/${key}`
  const manifestBytes = await fetchIfType(`${base}/manifest.json`, /json/)
  if (!manifestBytes) return null
  let parsed
  try { parsed = JSON.parse(new TextDecoder().decode(manifestBytes)) } catch { return null }
  const captures = []
  for (const cap of parsed.captures ?? []) {
    const bytes = await fetchIfType(`${base}/${cap.i}.png`, /^image\//)
    if (!bytes) return null  // incomplete set → don't ship a broken half
    captures.push({ i: cap.i, bytes })
  }
  return { manifestBytes, captures }
}

function assetSplatUrl(asset) {
  return asset.url ?? asset.fileUrl ?? asset.file_url ?? asset.raw?.file_url ?? asset.raw?.fileUrl
}
function assetPose(asset) {
  const raw = asset.defaultPose ?? asset.raw?.default_pose ?? asset.raw?.defaultPose ?? {}
  if (typeof raw === 'string') { try { return JSON.parse(raw) } catch { return {} } }
  return raw ?? {}
}

// Builds the package bytes. Throws loudly on any failure (CLAUDE.md: no silent
// partial package). Returns { packageName, fileName, bytes, hasIbl }.
export async function buildEnvironmentExport(asset, { version = '1.0.0', onProgress } = {}) {
  const splatUrl = assetSplatUrl(asset)
  if (!splatUrl) throw new Error('environment export: asset has no splat URL')

  // 1. Splat — the big download. Byte-accurate progress.
  const splatBytes = await fetchBytesWithProgress(splatUrl, onProgress, 'splat')

  // 2. Lighting datasets, keyed by the same splatCacheKey. Each is
  // optional-but-loud: bundled when baked, cleanly omitted when not.
  report(onProgress, { phase: 'lighting', label: 'lighting', loaded: 0, total: 0 })
  const key = await splatCacheKey(splatUrl)
  const iblBytes = await resolveIblBytes(key)
  const lightprobeBytes = await resolveLightprobeBytes(key)
  const reflections = await resolveReflectionSet(key)

  const splatFormat = (asset.format ?? splatUrl.split('.').pop() ?? 'ply').toLowerCase()
  const id = asset.id ?? asset.rawId ?? 'env'
  const name = asset.name ?? asset.raw?.name ?? 'environment'

  // 3. Assemble the tar.
  report(onProgress, { phase: 'packaging', label: 'packaging', loaded: 0, total: 0 })
  const { packageName, files } = buildEnvironmentPackage({
    id, name, version, splatFormat, pose: assetPose(asset),
    splatBytes, iblBytes, lightprobeBytes, reflections,
  })
  const tar = buildTar(files)

  // 4. Compress off the main thread.
  report(onProgress, { phase: 'compressing', label: 'compressing', loaded: 0, total: 0 })
  const bytes = await gzipAsync(tar)

  report(onProgress, { phase: 'done', label: 'done', loaded: 1, total: 1 })
  const fileName = `${packageName.replace('@', '').replace('/', '-')}-${version}.tgz`
  return {
    packageName, fileName, bytes,
    hasIbl: !!iblBytes,
    hasLightprobes: !!lightprobeBytes,
    hasReflections: !!reflections,
  }
}

// Triggers a browser download of the .tgz.
export async function downloadEnvironmentExport(asset, opts) {
  const { fileName, bytes, ...rest } = await buildEnvironmentExport(asset, opts)
  const blob = new Blob([bytes], { type: 'application/gzip' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = fileName
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
  return { fileName, ...rest }
}

// Test/agent hook: returns the raw bytes as base64 (no download) so Playwright
// can inspect the package without intercepting a file download.
if (typeof window !== 'undefined') {
  window.__EXPORT_ENVIRONMENT_PACKAGE__ = async (asset, opts) => {
    const { bytes, fileName, packageName, hasIbl, hasLightprobes, hasReflections } =
      await buildEnvironmentExport(asset, opts)
    let bin = ''
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
    return { base64: btoa(bin), fileName, packageName, hasIbl, hasLightprobes, hasReflections }
  }
}
